import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Droplets, Sun, Filter, CloudRain, Home as HomeIcon, Sprout, Zap, Recycle,
  CheckCircle, ArrowRight
} from 'lucide-react';
import { useT, RichText } from '../i18n/I18nContext';

const WATER_TECH_IMAGE = "https://images.unsplash.com/photo-1651025674254-bd69d5d550f0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxhZnJpY2FuJTIwY29tbXVuaXR5JTIwd2F0ZXIlMjBwdW1wJTIwbWFudWFsJTIwZHJpbGxpbmd8ZW58MHx8fHwxNzcyNzk4NDYxfDA&ixlib=rb-4.1.0&q=85";

const cardIcons = [Droplets, Sun, Filter, CloudRain, HomeIcon, Sprout, Zap, Recycle];
const cardColors = ['bg-blue-500', 'bg-amber-500', 'bg-cyan-500', 'bg-indigo-500', 'bg-green-500', 'bg-emerald-500', 'bg-yellow-500', 'bg-teal-500'];

export default function SMARTechsPage() {
  const { t } = useT();
  const cards = (t('smartechs.cards') || []).map((c, i) => ({
    ...c,
    Icon: cardIcons[i] || Droplets,
    color: cardColors[i] || 'bg-blue-500',
  }));
  const benefits = t('smartechs.principle.benefits') || [];

  return (
    <div data-testid="smartechs-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-primary" data-testid="smartechs-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-secondary font-semibold text-sm uppercase tracking-wider">{t('smartechs.hero.eyebrow')}</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6 font-heading">
              {t('smartechs.hero.title')}
            </h1>
            <p className="text-xl text-blue-100">{t('smartechs.hero.subtitle')}</p>
          </motion.div>
        </div>
      </section>

      {/* Principle / Benefits */}
      <section className="py-16 md:py-20 bg-accent" data-testid="key-benefits">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-secondary font-semibold text-sm uppercase tracking-wider">{t('smartechs.principle.eyebrow')}</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-4 font-heading">
                {t('smartechs.principle.title')}
              </h2>
              <RichText text={t('smartechs.principle.body')} className="block text-slate-600 leading-relaxed mb-6" />
              <div className="space-y-3">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={benefit}
                    className="flex items-center gap-3"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.08 }}
                  >
                    <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0" />
                    <span className="text-slate-700">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src={WATER_TECH_IMAGE}
                alt="Water pump technology"
                className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Technologies Grid */}
      <section className="py-16 md:py-24 bg-white" data-testid="technologies-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-heading">
              {t('smartechs.grid.title')}
            </h2>
            <p className="text-lg text-slate-600 mt-4 max-w-2xl mx-auto">
              {t('smartechs.grid.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {cards.map((tech, index) => (
              <motion.div
                key={tech.title}
                className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`technology-card-${index}`}
              >
                <div className={`${tech.color} p-6`}>
                  <tech.Icon className="h-12 w-12 text-white" />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-3 font-heading">{tech.title}</h3>
                  <p className="text-slate-600 mb-4">{tech.description}</p>
                  <ul className="space-y-2">
                    {(tech.features || []).map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-sm text-slate-600">
                        <CheckCircle className="h-4 w-4 text-secondary flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-heading">
            {t('smartechs.cta.title')}
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            {t('smartechs.cta.body')}
          </p>
          <Link
            to="/trainings"
            className="inline-flex items-center gap-2 bg-white text-secondary hover:bg-green-50 rounded-full px-8 py-4 font-semibold text-lg transition-all shadow-lg"
            data-testid="smartechs-cta"
          >
            {t('smartechs.cta.button')} <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
